import React from 'react';
import {
  Box,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import RatingDelta from './RatingDelta';
import { medalColor } from './tierColors';
import { QuizResultRow } from '../../types/ranked';

export interface QuizResultsTableProps {
  results: QuizResultRow[];
  loading?: boolean;
  emptyMessage?: string;
}

/**
 * Every quiz a student took, live and self-paced together.
 *
 * Both modes share one table rather than splitting into two because a parent
 * asks "how is my child doing at quizzes", not "how is my child doing at each
 * of two delivery mechanisms". The mode is a column, not a section.
 *
 * A live quiz whose field was below three mathletes appears with "Didn't
 * count" rather than being hidden: it happened, the child played it, and
 * omitting it would misreport their activity.
 */
const QuizResultsTable: React.FC<QuizResultsTableProps> = ({
  results,
  loading = false,
  emptyMessage = 'No quizzes yet.'
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {[0, 1, 2].map(row => (
          <Skeleton key={row} variant="rounded" height={40} />
        ))}
      </Box>
    );
  }

  if (results.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {emptyMessage}
      </Typography>
    );
  }

  return (
    // Wide content scrolls inside its own container so the page never scrolls
    // sideways on a phone.
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Quiz</TableCell>
            <TableCell>Mode</TableCell>
            <TableCell>Date</TableCell>
            <TableCell align="right">Score</TableCell>
            <TableCell align="right">Placement</TableCell>
            <TableCell align="right">Rating</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map(result => {
            const medal = result.placement != null ? medalColor(result.placement) : null;

            return (
              <TableRow key={result.attemptId} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {result.quizTitle}
                  </Typography>
                  {!result.completed && (
                    <Typography variant="caption" color="text.secondary">
                      Unfinished
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Chip
                    size="small"
                    variant={result.mode === 'live' ? 'filled' : 'outlined'}
                    color={result.mode === 'live' ? 'error' : 'default'}
                    label={result.mode === 'live' ? 'Live' : 'Self-paced'}
                  />
                </TableCell>

                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(result.playedAt)}
                  </Typography>
                </TableCell>

                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}
                  >
                    {result.score}
                  </Typography>
                </TableCell>

                <TableCell align="right">
                  {result.placement != null && result.fieldSize != null ? (
                    <Typography
                      variant="body2"
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: medal ? 700 : 400,
                        color: medal ?? 'text.primary'
                      }}
                    >
                      {ordinal(result.placement)} of {result.fieldSize}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>

                <TableCell align="right">
                  {result.rated && result.delta != null ? (
                    <RatingDelta delta={result.delta} />
                  ) : (
                    <Tooltip
                      title={
                        result.mode === 'live'
                          ? 'A live quiz needs 3 or more mathletes to affect a rating.'
                          : 'Only live quizzes affect a rating.'
                      }
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.disabled, whiteSpace: 'nowrap' }}
                      >
                        Didn’t count
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ordinal(n: number): string {
  const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
}

export default QuizResultsTable;
